/**
 * 对话页 KeepAlive（保活）容器
 *
 * 背景：
 * conversation 目录下的 4 个对话页原本是 <Routes> 里的独立路由，一旦离开
 * （返回上一页 / 切到其它路由）组件即被卸载，ChatArea 内部的消息列表、输入框、
 * 滚动位置、语音播报等状态会全部丢失，再次进入只能从零开始。
 *
 * 方案：
 * 把这 4 个对话页从 <Routes> 中「摘出来」，改由本容器常驻渲染：
 *   1. 首次进入某个对话页时正常挂载其组件；
 *   2. 离开时不再卸载，仅把外层容器 display 置为 none（隐藏但保持挂载）；
 *   3. 再次进入且属于同一段会话时，复用同一个实例，完整保留会话状态。
 *
 * 会话身份（identity）与缓存失效规则：
 * 身份只由「真正决定一段会话」的字段决定，而不是整份 location.state：
 *   1. 携带 sessionId（从历史记录进入）-> 身份 = session:<sessionId>；
 *      与缓存身份不同则重建，从而重新拉取该历史会话的消息。
 *   2. 携带 fileList（拍照 / 上传后发起咨询）-> 身份为空串，延续当前会话，复用缓存
 *      实例，把这张图追加到已有会话里。「每次上传都发一条消息」由 ChatArea 自己保证：
 *      它按「本次导航的 location.key + 文件内容特征」去重，同一份上传只发一次，
 *      新的一次上传（key 或内容变化）会重新发送。
 *   3. 其它（功能页「AI 咨询 / 生成方案」入口，state 里是 chatPayload / date 等
 *      一次性入参）-> 身份为空串，表示「延续该对话页的当前会话」，直接复用缓存实例。
 *
 * 为什么不能直接拿整份 location.state 当身份：
 * 历史记录入口的 state 是 { sessionId, healthDisease, date, fromHistory }，
 * 功能页入口的 state 是 { disease, ratio, chatPayload } / { date, chatPayload } /
 * { chatPayload, profileName } —— 两者字段完全不同，按整份 state 比对必然被判成
 * 「另一个会话」，于是旧实例被销毁重建；而新实例又没有 sessionId，ChatArea 不会去
 * 拉历史消息，最终表现为「从历史记录进来 -> 返回功能页 -> 再点 AI 咨询进来，消息全没了」。
 *
 * 兜底：
 * 为了让路由仍能命中这些 path（避免落到 * 兜底路由），App.js 中为这些 path 注册了
 * element 为 null 的占位路由，实际页面由本容器渲染。
 */
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ROUTE_PATHS } from "./RouterPaths";

const LazyConversationKetogenicDiet = lazy(() =>
    import("../pages/conversation/KetogenicDiet/index"),
);
const LazyConversationNutritionAssessment = lazy(() =>
    import("../pages/conversation/NutritionAssessment/index"),
);
const LazyConversationRecipeCustomization = lazy(() =>
    import("../pages/conversation/RecipeCustomization/index"),
);
const LazyConversationSportNutrition = lazy(() =>
    import("../pages/conversation/SportNutrition/index"),
);

/**
 * 需要保活的对话页配置
 * - path:      路由路径，与 RouterPaths 保持一致
 * - cacheKey:  缓存实例唯一标识
 * - Component: 对话页组件，会收到 active 属性（当前是否处于激活/可见状态）
 */
export const CONVERSATION_KEEP_ALIVE_ROUTES = [
    {
        path: ROUTE_PATHS.ConversationKetogenicDiet,
        cacheKey: "conversation:ketogenicDiet",
        Component: LazyConversationKetogenicDiet,
    },
    {
        path: ROUTE_PATHS.ConversationNutritionAssessment,
        cacheKey: "conversation:nutritionAssessment",
        Component: LazyConversationNutritionAssessment,
    },
    {
        path: ROUTE_PATHS.ConversationRecipeCustomization,
        cacheKey: "conversation:recipeCustomization",
        Component: LazyConversationRecipeCustomization,
    },
    {
        path: ROUTE_PATHS.ConversationSportNutrition,
        cacheKey: "conversation:sportNutrition",
        Component: LazyConversationSportNutrition,
    },
];

function getConversationRouteByPath(pathname) {
    return (
        CONVERSATION_KEEP_ALIVE_ROUTES.find((route) => route.path === pathname) ||
        null
    );
}

/**
 * 计算会话身份标识（identity）。返回空串表示「延续该对话页的当前会话」。
 * 具体规则见文件头部的「会话身份与缓存失效规则」。
 */
function getConversationIdentity(routeState) {
    if (routeState === null || routeState === undefined) return "";
    // 1. 历史记录进入：以 sessionId 作为会话身份
    const { sessionId } = routeState;
    if (sessionId !== null && sessionId !== undefined && sessionId !== "") {
        return `session:${sessionId}`;
    }
    // 2. 携带待发送文件（拍照 / 上传后发起咨询）：延续当前会话，复用实例
    // 3. 功能页「AI 咨询 / 生成方案」入口：延续当前会话
    return "";
}

/**
 * 判断是否需要为该对话页重建实例。
 * - 身份为空串：属于「延续当前会话」，只要已存在实例就复用，绝不重建；
 * - 身份非空：与缓存身份一致才复用，否则重建（切换到另一段会话）。
 */
function needRebuildInstance(cached, identity) {
    if (!cached) return true;
    if (!identity) return false;
    return cached.identity !== identity;
}

const KEEP_ALIVE_WRAPPER_STYLE = {
    flex: 1,
    minHeight: 0,
    flexDirection: "column",
    overflow: "hidden",
};

export default function ConversationKeepAlive() {
    const { pathname, state } = useLocation();
    const activeRoute = getConversationRouteByPath(pathname);
    const activeCacheKey = activeRoute ? activeRoute.cacheKey : "";
    const activeIdentity = activeRoute ? getConversationIdentity(state) : "";

    // 已缓存的实例：{ cacheKey, identity }，同一个对话页只保留最近一次身份的实例
    const [instances, setInstances] = useState(() =>
        activeCacheKey ? [{ cacheKey: activeCacheKey, identity: activeIdentity }] : [],
    );

    useEffect(() => {
        if (!activeCacheKey) return;
        setInstances((prev) => {
            const cached = prev.find((item) => item.cacheKey === activeCacheKey);
            if (!needRebuildInstance(cached, activeIdentity)) return prev;
            // 会话身份变化：丢弃该对话页的旧实例，重建新实例
            return [
                ...prev.filter((item) => item.cacheKey !== activeCacheKey),
                { cacheKey: activeCacheKey, identity: activeIdentity },
            ];
        });
    }, [activeCacheKey, activeIdentity]);

    // 首次进入 / 身份刚变化时，立即渲染新实例，避免等 effect 生效造成空白
    const renderInstances = useMemo(() => {
        if (!activeCacheKey) return instances;
        const cached = instances.find((item) => item.cacheKey === activeCacheKey);
        if (!needRebuildInstance(cached, activeIdentity)) return instances;
        return [
            ...instances.filter((item) => item.cacheKey !== activeCacheKey),
            { cacheKey: activeCacheKey, identity: activeIdentity },
        ];
    }, [activeCacheKey, activeIdentity, instances]);

    if (renderInstances.length === 0) return null;

    return (
        <>
            {renderInstances.map(({ cacheKey, identity }) => {
                const route = CONVERSATION_KEEP_ALIVE_ROUTES.find(
                    (item) => item.cacheKey === cacheKey,
                );
                if (!route) return null;
                const Page = route.Component;
                const isActive = cacheKey === activeCacheKey;
                return (
                    // 保活包裹层：激活时是纵向 flex 容器（撑满剩余高度，保证页面内部
                    // height:100% / flex:1 生效），失活时仅 display:none 不卸载。
                    // key 带上 identity：身份变化时 React 会卸载旧实例、挂载新实例。
                    <div
                        key={`${cacheKey}::${identity}`}
                        className="conversation-keep-alive"
                        style={{
                            ...KEEP_ALIVE_WRAPPER_STYLE,
                            display: isActive ? "flex" : "none",
                        }}
                    >
                        <Suspense fallback={null}>
                            <Page active={isActive} />
                        </Suspense>
                    </div>
                );
            })}
        </>
    );
}
